import { useState } from 'react';
import {
  Box, Paper, TextField, Button, Typography, Alert, Grid, Stack, Divider,
  Stepper, Step, StepLabel, List, ListItem, ListItemIcon, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import api from '../services/api';

const REQUIRED_DOCS = [
  { key: 'CAC_CERTIFICATE', label: 'CAC Certificate' },
  { key: 'TAX_CLEARANCE', label: 'Tax Clearance' },
  { key: 'PENCOM_CERTIFICATE', label: 'PENCOM Certificate' },
  { key: 'NSITF_CERTIFICATE', label: 'NSITF Certificate' },
  { key: 'ITF_CERTIFICATE', label: 'ITF Certificate' },
];

const STEPS = ['Company Information', 'Compliance Documents', 'Login Credentials', 'Review & Submit'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Keep only digits, capped at maxLen â€” used for phone / account-style fields.
const digitsOnly = (v, maxLen) => v.replace(/\D/g, '').slice(0, maxLen);

export default function VendorRegister() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState({
    companyName: '', cacNumber: '', tin: '', contactPerson: '',
    contactPhone: '', email: '', password: '', confirmPassword: '',
  });
  const [files, setFiles] = useState({});
  // Documents already saved in a draft (server-side), keyed by docType -> fileName.
  // The browser cannot re-populate a file picker, so these are shown as "on file"
  // and count as satisfied; the backend pulls them from the draft on submit.
  const [savedDocs, setSavedDocs] = useState({});
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Save / Resume dialog state. dialog is 'save' | 'resume' | null.
  const [dialog, setDialog] = useState(null);
  const [dialogEmail, setDialogEmail] = useState('');
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  // Phone is numeric only, max 11 digits (Nigerian mobile format).
  const setPhone = (e) => setForm((f) => ({ ...f, contactPhone: digitsOnly(e.target.value, 11) }));
  const setFile = (k) => (e) => setFiles((f) => ({ ...f, [k]: e.target.files[0] }));

  // A document is satisfied by either a fresh upload or a draft-saved copy.
  const docSatisfied = (key) => Boolean(files[key]) || Boolean(savedDocs[key]);
  const uploadedCount = REQUIRED_DOCS.filter((d) => docSatisfied(d.key)).length;

  // Per-step validation â€” returns an error string, or '' if the step is valid.
  const validateStep = (step) => {
    if (step === 0) {
      if (!form.companyName.trim()) return 'Company name is required.';
      if (!form.cacNumber.trim()) return 'CAC registration number is required.';
      // Same rule as the server: the type (RC, BN or IT), then the digits.
      if (!/^\s*(RC|BN|IT)\s*(NO\.?|NUMBER)?[\s.\-\/:#]*\d{1,10}\s*$/i.test(form.cacNumber)) {
        return 'Enter the CAC number with its type, e.g. RC 1234567, BN 1234567 or IT 12345.';
      }
      if (!form.tin.trim()) return 'TIN is required.';
      if (!form.contactPerson.trim()) return 'Contact person is required.';
      if (form.contactPhone && form.contactPhone.length !== 11) return 'Phone number must be 11 digits.';
    }
    if (step === 1) {
      const missing = REQUIRED_DOCS.filter((d) => !docSatisfied(d.key));
      if (missing.length) return `Please upload: ${missing.map((d) => d.label).join(', ')}`;
    }
    if (step === 2) {
      if (!EMAIL_RE.test(form.email)) return 'A valid email is required.';
      if (form.password.length < 8) return 'Password must be at least 8 characters.';
      if (form.password !== form.confirmPassword) return 'Passwords do not match.';
    }
    return '';
  };

  const next = () => {
    const msg = validateStep(activeStep);
    if (msg) { setError(msg); return; }
    setError('');
    setActiveStep((s) => s + 1);
  };
  const back = () => { setError(''); setActiveStep((s) => s - 1); };

  const submit = async () => {
    // Final guard across all steps.
    for (let s = 0; s <= 2; s++) {
      const msg = validateStep(s);
      if (msg) { setError(msg); setActiveStep(s); return; }
    }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => { if (k !== 'confirmPassword') fd.append(k, v); });
    fd.append('fullName', form.contactPerson);
    // Only append docs the user actually re-picked this session. Anything left
    // out is filled from their saved draft server-side.
    REQUIRED_DOCS.forEach((d) => { if (files[d.key]) fd.append(d.key, files[d.key]); });

    setBusy(true);
    setError('');
    try {
      await api.post('/vendors/register', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDone(true);
    } catch (err) {
      const status = err.response?.status;
      const backendMsg = err.response?.data?.error?.message;
      let msg;
      if (status === 413) {
        msg = 'Your uploaded documents are too large. Please keep the total size under 25MB and try again.';
      } else if (!err.response) {
        msg = 'Could not reach the server. Check your internet connection and try again.';
      } else {
        msg = backendMsg || `Registration failed (error ${status || 'unknown'}). Please review your details and try again.`;
      }
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  // ---- Save & Continue Later -------------------------------------------------
  const openSave = () => {
    setDialogError('');
    setDialogEmail(form.email || '');
    setDialog('save');
  };

  const doSave = async () => {
    const email = dialogEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { setDialogError('A valid email is required to save your progress.'); return; }

    const fd = new FormData();
    fd.append('email', email);
    ['companyName', 'cacNumber', 'tin', 'contactPerson', 'contactPhone'].forEach((k) => fd.append(k, form[k] || ''));
    REQUIRED_DOCS.forEach((d) => { if (files[d.key]) fd.append(d.key, files[d.key]); });

    setDialogBusy(true);
    setDialogError('');
    try {
      await api.post('/drafts/save', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      // Reflect newly-saved documents as "on file" so they show as satisfied.
      setSavedDocs((prev) => {
        const nextS = { ...prev };
        REQUIRED_DOCS.forEach((d) => { if (files[d.key]) nextS[d.key] = files[d.key].name; });
        return nextS;
      });
      setForm((f) => ({ ...f, email: f.email || email }));
      setDialog(null);
      setError('');
      setNotice(`Progress saved. Resume anytime by choosing "Resume a saved application" and entering ${email}.`);
    } catch (err) {
      setDialogError(err.response?.data?.error?.message || 'Could not save your progress. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  };

  // ---- Resume a saved application -------------------------------------------
  const openResume = () => {
    setDialogError('');
    setDialogEmail('');
    setDialog('resume');
  };

  const doResume = async () => {
    const email = dialogEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { setDialogError('Enter the email you used when you saved.'); return; }

    setDialogBusy(true);
    setDialogError('');
    try {
      const { data } = await api.get(`/drafts/${encodeURIComponent(email)}`);
      const d = data.data;
      const fd = d.formData || {};
      setForm((f) => ({
        ...f,
        companyName: fd.companyName || '',
        cacNumber: fd.cacNumber || '',
        tin: fd.tin || '',
        contactPerson: fd.contactPerson || '',
        contactPhone: fd.contactPhone || '',
        email,
      }));
      const sd = {};
      (d.savedDocuments || []).forEach((x) => { sd[x.docType] = x.fileName; });
      setSavedDocs(sd);
      setFiles({});
      setActiveStep(0);
      setDialog(null);
      setError('');
      setNotice('Draft loaded. Review your details and continue â€” you will re-enter your password on the Login Credentials step.');
    } catch (err) {
      const status = err.response?.status;
      setDialogError(status === 404
        ? 'No saved application was found for that email.'
        : (err.response?.data?.error?.message || 'Could not load your saved application. Please try again.'));
    } finally {
      setDialogBusy(false);
    }
  };

  // Success confirmation screen (before sending them to sign in).
  if (done) {
    return (
      <PublicLayout maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Paper sx={{ p: 5, textAlign: 'center', maxWidth: 480 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>Registration successful</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your account has been created and is now pending review. You can sign in to track your
              status; you'll be able to submit bids once an administrator approves your account.
            </Typography>
            <Button variant="contained" size="large" onClick={() => navigate('/login')}>Go to sign in</Button>
          </Paper>
        </Box>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <Box sx={{ maxWidth: 820, mx: 'auto' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
          <Typography variant="h5" color="primary" gutterBottom sx={{ fontWeight: 600 }}>Vendor Registration</Typography>
          <Button size="small" onClick={openResume}>Resume a saved application</Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Complete the steps below to register your company. All five compliance documents are required.
          You can save your progress at any time and finish later.
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>

        {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper sx={{ p: 3, mb: 3 }}>
          {activeStep === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField label="Company Name" value={form.companyName} onChange={set('companyName')} fullWidth required /></Grid>
              <Grid item xs={12} sm={6}><TextField label="CAC Registration Number" value={form.cacNumber} onChange={set('cacNumber')} fullWidth required placeholder="e.g. RC 1234567" helperText="Include the type: RC (company), BN (business name) or IT (incorporated trustees)." /></Grid>
              <Grid item xs={12} sm={6}><TextField label="TIN" value={form.tin} onChange={set('tin')} fullWidth required /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Contact Person" value={form.contactPerson} onChange={set('contactPerson')} fullWidth required /></Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Phone Number" value={form.contactPhone} onChange={setPhone} fullWidth
                  inputProps={{ inputMode: 'numeric' }} helperText="11 digits, e.g. 08012345678" />
              </Grid>
            </Grid>
          )}

          {activeStep === 1 && (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1">Upload your compliance documents</Typography>
                <Typography variant="body2" color={uploadedCount === 5 ? 'success.main' : 'text.secondary'}>{uploadedCount} / 5 uploaded</Typography>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                {REQUIRED_DOCS.map((d) => {
                  const fresh = files[d.key];
                  const onFile = savedDocs[d.key];
                  const satisfied = Boolean(fresh) || Boolean(onFile);
                  return (
                    <Grid item xs={12} sm={6} key={d.key}>
                      <Button component="label" variant={satisfied ? 'contained' : 'outlined'} color={satisfied ? 'success' : 'primary'} fullWidth sx={{ justifyContent: 'flex-start' }}>
                        {satisfied ? `âœ“ ${d.label}${fresh ? '' : ' (on file)'}` : `Upload ${d.label}`}
                        <input type="file" hidden accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={setFile(d.key)} />
                      </Button>
                      {fresh && <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>{fresh.name}</Typography>}
                      {!fresh && onFile && <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>{onFile} â€” saved. Upload again to replace.</Typography>}
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}

          {activeStep === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}><TextField label="Email" type="email" value={form.email} onChange={set('email')} fullWidth required /></Grid>
              <Grid item xs={12} sm={4}><TextField label="Password" type="password" value={form.password} onChange={set('password')} fullWidth required helperText="At least 8 characters" /></Grid>
              <Grid item xs={12} sm={4}><TextField label="Confirm Password" type="password" value={form.confirmPassword} onChange={set('confirmPassword')} fullWidth required /></Grid>
            </Grid>
          )}

          {activeStep === 3 && (
            <>
              <Typography variant="subtitle1" gutterBottom>Review your details</Typography>
              <List dense>
                <ListItem><ListItemText primary="Company" secondary={form.companyName} /></ListItem>
                <ListItem><ListItemText primary="CAC Number" secondary={form.cacNumber} /></ListItem>
                <ListItem><ListItemText primary="TIN" secondary={form.tin} /></ListItem>
                <ListItem><ListItemText primary="Contact Person" secondary={form.contactPerson} /></ListItem>
                <ListItem><ListItemText primary="Phone" secondary={form.contactPhone || 'â€”'} /></ListItem>
                <ListItem><ListItemText primary="Email" secondary={form.email} /></ListItem>
              </List>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Documents</Typography>
              <List dense>
                {REQUIRED_DOCS.map((d) => (
                  <ListItem key={d.key}>
                    <ListItemIcon sx={{ minWidth: 34 }}>
                      {docSatisfied(d.key) ? <CheckCircleIcon color="success" fontSize="small" /> : <RadioButtonUncheckedIcon color="disabled" fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText primary={d.label} secondary={files[d.key]?.name || savedDocs[d.key]} />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Paper>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button disabled={activeStep === 0} onClick={back}>Back</Button>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={openSave}>Save &amp; Continue Later</Button>
            {activeStep < STEPS.length - 1
              ? <Button variant="contained" onClick={next}>Next</Button>
              : <Button variant="contained" onClick={submit} disabled={busy}>{busy ? 'Submittingâ€¦' : 'Submit Registration'}</Button>}
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Button component={RouterLink} to="/login" size="small">Already registered? Sign in</Button>
        </Stack>
      </Box>

      {/* Save / Resume dialog */}
      <Dialog open={dialog !== null} onClose={() => !dialogBusy && setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{dialog === 'save' ? 'Save & Continue Later' : 'Resume a saved application'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {dialog === 'save'
              ? 'Enter an email to save your progress under. You can resume on any device with this email. Your password is never saved.'
              : 'Enter the email you used when you saved your progress.'}
          </DialogContentText>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
          <TextField
            label="Email"
            type="email"
            value={dialogEmail}
            onChange={(e) => setDialogEmail(e.target.value)}
            fullWidth
            autoFocus
            disabled={dialogBusy}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={dialogBusy}>Cancel</Button>
          {dialog === 'save'
            ? <Button variant="contained" onClick={doSave} disabled={dialogBusy}>{dialogBusy ? 'Savingâ€¦' : 'Save'}</Button>
            : <Button variant="contained" onClick={doResume} disabled={dialogBusy}>{dialogBusy ? 'Loadingâ€¦' : 'Load draft'}</Button>}
        </DialogActions>
      </Dialog>
    </PublicLayout>
  );
}

